/**
 * @file `RequestUnion` handler for the sibling POST that resolves a pending
 * `approvalRequired` event.
 *
 * The original agentic stream is still open on its own connection, suspended
 * server-side via `awaitApproval` in `cloud_run/.../approvalRegistry.ts`.
 * The server responds with an `AgenticApprovalResolvedResponse` ack (carrying
 * `newFormVersionId` when a new form version was created at the gate), then
 * on approve the staged tool runs and the loop continues; on cancel the
 * harness injects a `{ ok: false, cancelled: true }` tool result so the
 * model can replan or `finish` — the loop is not killed. All subsequent
 * events flow back over the original SSE connection — not the response to
 * this request.
 *
 * Consumers: `InlineApproval` in `MessagesList.tsx` and
 * `MappingReviewApprove`, dispatched through `callRunLanguageModel`.
 * Ordered first in `cloudRunHandlers` because the catch-all `streamHandler`
 * would otherwise claim it.
 */
import type { AgenticApprovalResolvedRequest, AgenticApprovalResolvedResponse } from '#types';
import type { ResponseHandler } from '../types';
export const approvalResolvedHandler: ResponseHandler<AgenticApprovalResolvedRequest> = {
    isApplicable: (r): r is AgenticApprovalResolvedRequest => 'approvalResolved' in r,
    handle: async (response, _request, { onComplete }) => {
        const result = (await response.json().catch(() => undefined)) as
            | AgenticApprovalResolvedResponse
            | undefined;
        onComplete?.(result);
        return result;
    },
};
